// ═══════════════════════════════════════════════════════════════════════════
//  RELEASE NOTES — every PWA deploy that changes what a user gets adds an
//  entry here. `npm run deploy` refuses to publish without one.
// ═══════════════════════════════════════════════════════════════════════════
//
// The first time someone opens the app after a deploy, the service worker
// swaps in the new build and reloads (see index.tsx), and the «تازه‌های همیان»
// sheet then shows them every entry newer than the last one they saw. So what
// is written here is literally what users read. It is also the version in the
// sidebar and the `app_version` property on every PostHog event.
//
// ── HOW TO SHIP A VERSION ──────────────────────────────────────────────────
//
//  1. Add ONE new entry at the TOP of RELEASES (newest first). Several merges
//     going out in one deploy share one entry, one line each.
//  2. Pick the version from the entry below it (the rules are enforced):
//
//       fix    1.4.2 → 1.4.3   only fixes / polish of things users already had
//       minor  1.4.2 → 1.5.0   anything a user can newly see or do. Required
//                              as soon as one change is `new`.
//       major  1.4.2 → 2.0.0   the whole experience changes (a redesign).
//                              Only when the owner says so.
//
//     Exactly one step: no 1.4.2 → 1.6.0, no 1.4.2 → 1.5.1.
//  3. Build, then `npm run deploy`. It checks that the build carries this
//     version and that it is newer than what is live whenever src/ changed.
//     `node scripts/releases.mjs` runs the same rules without deploying.
//
// ── HOW TO WRITE IT ────────────────────────────────────────────────────────
//
//  - Persian, to the member, about what THEY notice. Not the commit message:
//      ✗ «fix(وام): useWizardHistory تاریخچه را خطی نگه دارد»
//      ✓ «دکمهٔ عقب در فرم درخواست وام، دقیقاً یک قدم برمی‌گردد.»
//  - `title` is the one line that makes the update feel worth it.
//  - A change nobody can notice (refactor, telemetry, build) does not get a
//    line. If a whole deploy is like that, say what it does for them —
//    «بارگذاری سریع‌تر صفحه‌ها» — or don't ship it on its own.
//  - No `→` in the text: it renders backwards in RTL. Use «از … به …».
//  - Never change the version of an entry that has been deployed. Fixing a
//    typo in its text is fine.
//
// This file must stay self-contained (no imports): the deploy script loads it
// outside webpack.

export type ChangeKind = 'new' | 'better' | 'fix'

export interface Change {
    /**
     * new «قابلیت جدید» — members could not see or do it before
     * better «بهبود» — something they had works nicer
     * fix «رفع مشکل» — something they had was broken
     */
    kind: ChangeKind
    text: string
}

export interface Release {
    /** major.minor.patch — see the rules above */
    version: string
    /** Gregorian YYYY-MM-DD of the deploy; shown to users in Jalali */
    date: string
    title: string
    changes: Change[]
}

export const RELEASES: Release[] = [
    {
        version: '1.16.0',
        date: '2026-09-21',
        title: 'همهٔ تراکنش‌های یک عضو، یک‌جا',
        changes: [
            {
                kind: 'new',
                text: '«همهٔ تراکنش‌های این عضو» در کارنامه: تب «تراکنش‌ها»ی صندوق، فقط با تراکنش‌های همان عضو.',
            },
            {
                kind: 'better',
                text: 'ردیف‌های «آخرین تراکنش‌ها» در کارنامه حالا قابل لمس‌اند؛ در کارنامهٔ خودتان هم «همهٔ تراکنش‌های شما» هست.',
            },
        ],
    },
    {
        version: '1.15.1',
        date: '2026-09-21',
        title: 'برگشت به صندوق، همان‌جایی که بودید',
        changes: [
            {
                kind: 'fix',
                text: 'در صفحهٔ صندوق، اگر از تب «وام‌ها» وامی را باز کنید و برگردید، دوباره روی همان تب «وام‌ها» هستید، نه «اعضا».',
            },
            {
                kind: 'fix',
                text: 'برگشتن از صفحهٔ یک عضو هم شما را به همان‌جای لیست اعضا می‌برد، با همان جست‌وجویی که کرده بودید.',
            },
            {
                kind: 'fix',
                text: 'کادر جست‌وجوی اعضا دیگر بعد از جست‌وجو خودبه‌خود بسته نمی‌شود.',
            },
        ],
    },
    {
        version: '1.15.0',
        date: '2026-09-21',
        title: 'همیان روی لپ‌تاپ و تبلت، جادارتر',
        changes: [
            {
                kind: 'better',
                text: 'روی لپ‌تاپ و تبلت، صفحه‌های همیان دیگر در ستونی باریک وسط صفحه فشرده نمی‌شوند و جای بیشتری دارند. روی گوشی چیزی عوض نشده است.',
            },
            {
                kind: 'better',
                text: 'روی صفحهٔ بزرگ، نوار پایین (خانه، صندوق‌ها، تراکنش‌ها و پیام‌ها) جمع‌وجور و وسط صفحه می‌نشیند.',
            },
        ],
    },
    {
        version: '1.14.3',
        date: '2026-09-21',
        title: 'پرداخت از نسخهٔ وب ایتا و بله',
        changes: [
            {
                kind: 'fix',
                text: 'در نسخهٔ وب ایتا و بله، صفحهٔ بانک دیگر سفید نمی‌ماند: درگاه در صفحهٔ جدید مرورگر باز می‌شود و بعد از پرداخت به همیان برمی‌گردید.',
            },
        ],
    },
    {
        version: '1.14.2',
        date: '2026-09-20',
        title: 'دیگر خودبه‌خود رفرش نمی‌شود',
        changes: [
            {
                kind: 'fix',
                text: 'در آیفون، صفحهٔ همیان گاهی پشت سر هم از نو باز می‌شد و کار نیمه‌تمام از دست می‌رفت؛ حالا فقط وقتی نسخهٔ تازه‌ای باشد صفحه نو می‌شود.',
            },
        ],
    },
    {
        version: '1.14.1',
        date: '2026-09-20',
        title: 'نمودار کارنامه، خواناتر',
        changes: [
            {
                kind: 'fix',
                text: 'در صفحهٔ «کارنامه»، سال‌های زیر نمودار سرمایه به انگلیسی نوشته می‌شد؛ حالا مثل بقیهٔ عددهای همیان، فارسی است.',
            },
            {
                kind: 'better',
                text: 'توضیح کنار همان نمودار هم ساده‌تر شد: به‌جای «مساحت زیر نمودار»، می‌گوید هرچه پول بیشتری مدت بیشتری در صندوق بماند، امتیاز بالاتر می‌رود.',
            },
        ],
    },
    {
        version: '1.14.0',
        date: '2026-09-20',
        title: 'پیامک را هم خودتان خاموش می‌کنید',
        changes: [
            {
                kind: 'new',
                text: 'در صفحهٔ «اعلان‌ها» کلید «پیامک» اضافه شد: اگر خبرها را در بله یا ایتا می‌گیرید و پیامک برایتان اضافه است، همان‌جا خاموشش کنید.',
            },
            {
                kind: 'better',
                text: 'زیر هر کلید نوشته‌ایم همین حالا خبرها از کجا به شما می‌رسد؛ و اگر همه را خاموش کنید، می‌گوییم خبرها همچنان در خود همیان و در «پیام‌ها» هست.',
            },
        ],
    },
    {
        version: '1.13.2',
        date: '2026-09-20',
        title: 'دکمهٔ بازگشت، همیشه کار می‌کند',
        changes: [
            {
                kind: 'fix',
                text: 'وقتی از پیامک، اعلان یا لینک یک صفحه را مستقیم باز می‌کردید، فلش بازگشت گوشهٔ صفحه کار نمی‌کرد؛ حالا شما را به صفحهٔ اصلی همیان می‌برد.',
            },
        ],
    },
    {
        version: '1.13.1',
        date: '2026-09-20',
        title: 'خبر هر نسخه، فقط یک بار',
        changes: [
            {
                kind: 'fix',
                text: 'پیام «همیان تازه شد» دیگر با هر بار باز کردن همیان تکرار نمی‌شود؛ هر نسخه یک بار. هر وقت خواستید از منو «تازه‌ها» را ببینید.',
            },
        ],
    },
    {
        version: '1.13.0',
        date: '2026-09-19',
        title: 'اجازهٔ اعلان، همان بار اول',
        changes: [
            {
                kind: 'better',
                text: 'کارت «فعال‌سازی اعلان‌ها» از صفحهٔ اصلی برداشته شد؛ حالا همان بار اول که همیان را باز می‌کنید، مرورگر یک بار اجازه را می‌پرسد.',
            },
            {
                kind: 'new',
                text: 'در صفحهٔ «اعلان‌ها» بخش «دسترسی اعلان در مرورگر» اضافه شد: هر وقت خواستید همان‌جا اجازه را می‌دهید یا پس می‌گیرید.',
            },
        ],
    },
    {
        version: '1.12.0',
        date: '2026-09-17',
        title: 'ورود با یک لمس در ایتا و بله',
        changes: [
            {
                kind: 'new',
                text: 'اگر همیان را داخل ایتا یا بله باز می‌کنید، با یک دکمه وارد شوید؛ بدون وارد کردن شماره و بدون کد پیامکی.',
            },
            {
                kind: 'new',
                text: 'اعلان‌های همیان در ایتا و بله، مستقیم همان صفحه‌ای را باز می‌کنند که پیام درباره‌اش بود.',
            },
            {
                kind: 'new',
                text: 'مدیران در «اطلاع‌رسانی به اعضا» می‌بینند همیان به هر عضو چه پیامی داده و از چه راهی: پیامک، پیام‌رسان یا اعلان.',
            },
        ],
    },
    {
        version: '1.11.0',
        date: '2026-09-17',
        title: 'تراکنش‌ها را بر اساس نوع فیلتر کنید',
        changes: [
            {
                kind: 'new',
                text: 'در «تراکنش‌ها» دکمهٔ فیلتر آمد: فقط واریزها، فقط برداشت‌ها، یا یک نوع مثل «پرداخت قسط» و «اعطای وام» را ببینید. تعداد نتیجه هم نوشته می‌شود.',
            },
            {
                kind: 'fix',
                text: 'جستجو با شماره همراه همان‌طور که می‌نویسید کار می‌کند: با صفر اول، بدون آن، یا با ارقام فارسی. در «اعضا» و «وام‌ها» هم همین‌طور.',
            },
            {
                kind: 'better',
                text: 'کادر جستجوی تراکنش‌ها یادآوری می‌کند که با #هشتگ هم می‌شود جستجو کرد.',
            },
        ],
    },
    {
        version: '1.10.0',
        date: '2026-09-17',
        title: 'از «ضمانت‌های من» مستقیم به اقساط همان وام',
        changes: [
            {
                kind: 'new',
                text: 'در صفحهٔ «ضمانت‌های من» با زدن روی هر وام، برنامهٔ اقساطش را می‌بینید و می‌توانید قسط‌های عقب‌افتاده را همان‌جا پرداخت کنید.',
            },
            {
                kind: 'fix',
                text: 'صفحهٔ «ضمانت‌های من» در مرورگر کامپیوتر دیگر تمام‌عرض نمی‌شود و مثل بقیهٔ صفحه‌ها می‌ماند.',
            },
        ],
    },
    {
        version: '1.9.0',
        date: '2026-09-17',
        title: '«پیگیری واریز» به جزئیات تراکنش آمد',
        changes: [
            {
                kind: 'new',
                text: 'در جزئیات هر وام یا برداشتی که همیان به حساب بانکی واریز کرده، «پیگیری واریز» هست: مدیر و گیرندهٔ پول می‌بینند پول کجاست، حتی بعد از واریز.',
            },
            {
                kind: 'better',
                text: 'جزئیات تراکنش هم‌شکل فهرست شد: مبلغ و وضعیت بالا، و بقیهٔ اطلاعات مرتب زیر آن. نام مدیری که تراکنش را ثبت کرده هم نوشته می‌شود.',
            },
            {
                kind: 'fix',
                text: 'دکمهٔ «اطلاعات وام» در جزئیات تراکنش حالا صفحهٔ همان وام را باز می‌کند.',
            },
            {
                kind: 'fix',
                text: 'زیر وام‌ها و قسط‌های ثبتی دیگر به‌اشتباه «مانده صندوق پس از تراکنش: ۰ تومان» نوشته نمی‌شود.',
            },
        ],
    },
    {
        version: '1.8.0',
        date: '2026-09-17',
        title: 'همهٔ تراکنش‌های صندوق، مرتب و قابل جستجو',
        changes: [
            {
                kind: 'new',
                text: 'حالا همهٔ تراکنش‌های صندوق را می‌بینید: با پایین رفتن در فهرست، قدیمی‌ترها هم می‌آیند. پیش از این فقط ۲۰ تای آخر دیده می‌شد.',
            },
            {
                kind: 'new',
                text: 'تراکنش‌ها را با نام عضو، شماره همراه یا بخشی از توضیحات جستجو کنید.',
            },
            {
                kind: 'better',
                text: 'تراکنش‌ها روزبه‌روز دسته‌بندی شده‌اند و هر ردیف جمع‌وجورتر و خواناتر است؛ برای دیدن جزئیات، روی خود ردیف بزنید.',
            },
            {
                kind: 'better',
                text: 'تب‌های «اعضا»، «تراکنش‌ها» و «وام‌ها» هنگام پایین رفتن در فهرست، بالای صفحه می‌مانند.',
            },
            {
                kind: 'fix',
                text: 'تراکنشی که مدیر دستی ثبت کرده، چه واریز باشد چه برداشت، همه‌جا با یک عنوان نشان داده می‌شود: «ثبت‌شده توسط مدیر».',
            },
        ],
    },
    {
        version: '1.7.1',
        date: '2026-09-17',
        title: 'کارنامهٔ متقاضی دقیق‌تر شد',
        changes: [
            {
                kind: 'fix',
                text: 'در خلاصهٔ کارنامهٔ متقاضی وام، عضوی که قسط‌هایش را با تأخیر داده دیگر سبز نشان داده نمی‌شود.',
            },
        ],
    },
    {
        version: '1.7.0',
        date: '2026-09-17',
        title: 'درخواست وام: پیگیری واریز و کارنامهٔ متقاضی در یک صفحه',
        changes: [
            {
                kind: 'new',
                text: 'اگر وام از موجودی نزد همیان پرداخت شده باشد، زیر «درخواست پذیرفته شد» لینک پیگیری واریز آمده و وضعیت واریز را همان‌جا می‌بینید.',
            },
            {
                kind: 'new',
                text: 'مدیران خلاصهٔ کارنامهٔ متقاضی را در صفحهٔ درخواست وام می‌بینند: امتیاز، سرمایه، مانده وام، معوق و نظم بازپرداخت، با یک لمس تا کارنامهٔ کامل.',
            },
            {
                kind: 'new',
                text: 'مدیران روی درخواست وام یادداشتی فقط برای مدیران می‌گذارند که عضو نمی‌بیند و پیامکی هم برایش نمی‌رود.',
            },
        ],
    },
    {
        version: '1.6.1',
        date: '2026-09-17',
        title: 'دکمهٔ پرداخت دیگر درخواست تکراری به بانک نمی‌فرستد',
        changes: [
            {
                kind: 'fix',
                text: 'بعد از زدن «پرداخت»، دکمه تا باز شدن صفحهٔ بانک قفل می‌ماند؛ زدن دوباره‌اش دیگر چند درخواست به درگاه نمی‌فرستد.',
            },
            {
                kind: 'fix',
                text: 'اگر برداشت وجه یا اعطای وام انجام نشود، دلیلش را می‌بینید؛ مثلاً «موجودی نزد همیان صندوق کافی نیست».',
            },
            {
                kind: 'better',
                text: 'صفحهٔ «جزئیات اطلاع‌رسانی» مرتب‌تر شد: تاریخ و ساعت به وقت ایران، و هزینهٔ بله فقط وقتی پیامی از بله رفته باشد.',
            },
        ],
    },
    {
        version: '1.6.0',
        date: '2026-09-16',
        title: 'اعلان نمی‌رسد؟ همین‌جا امتحانش کنید',
        changes: [
            {
                kind: 'new',
                text: '«ارسال اعلان آزمایشی» در صفحهٔ اعلان‌ها: اگر اعلان نرسید، می‌گوید کدام تنظیم گوشی یا کامپیوتر را درست کنید.',
            },
            {
                kind: 'fix',
                text: 'روشن کردن اعلان در بار اول دیگر بی‌صدا گیر نمی‌کند؛ نشان می‌دهد منتظر چیست.',
            },
        ],
    },
    {
        version: '1.5.0',
        date: '2026-09-16',
        title: 'صندوقی که بیرون از همیان دارید را بیاورید',
        changes: [
            {
                kind: 'new',
                text: 'دکمهٔ + در صفحهٔ صندوق‌ها حالا دو گزینه دارد: «ایجاد صندوق جدید» و «انتقال صندوق به همیان».',
            },
            {
                kind: 'new',
                text: 'اگر صندوقی از قبل دارید، بعد از تنظیمات صندوق مستقیم به ثبت سوابق مدیر می‌روید.',
            },
        ],
    },
    {
        version: '1.4.0',
        date: '2026-09-16',
        title: 'یادآوری‌ها و درخواست‌ها، با اعلان در همیان وب',
        changes: [
            {
                kind: 'new',
                text: 'با روشن کردن اعلان‌ها، سررسید حق عضویت و قسط، درخواستی که منتظر تأیید شماست و رسید پرداخت‌ها در مرورگر هم به شما خبر داده می‌شود.',
            },
            {
                kind: 'new',
                text: 'هفته‌ای یک بار، فقط اگر کاری منتظر شماست، یک یادآوری کوتاه می‌گیرید.',
            },
            {
                kind: 'new',
                text: 'از منوی «اعلان‌ها» انتخاب می‌کنید چه چیزهایی خبر داده شود. شب‌ها فقط رسید پرداخت‌ها، آن هم بی‌صدا، می‌رسد.',
            },
        ],
    },
    {
        version: '1.3.0',
        date: '2026-09-15',
        title: 'هدف صندوق را از همان اول بگویید',
        changes: [
            {
                kind: 'new',
                text: 'موقع ساختن صندوق قرض‌الحسنه یا قرعه‌کشی، هدف صندوق را انتخاب می‌کنید و اگر خواستید توضیح کوتاهی هم می‌نویسید.',
            },
        ],
    },
    {
        version: '1.2.1',
        date: '2026-09-15',
        title: 'اعطای وام نشان می‌دهد به کدام درخواست وصل می‌شود',
        changes: [
            {
                kind: 'better',
                text: 'در «اعطای وام جدید»، اگر عضو درخواست وام داشته باشد، مدیر از قدم اول می‌بیند که وام به همان درخواست وصل می‌شود.',
            },
            {
                kind: 'better',
                text: 'در فهرست گیرندگان وام، مانده وام هر عضو با همین نام نوشته می‌شود.',
            },
        ],
    },
    {
        version: '1.2.0',
        date: '2026-09-15',
        title: 'وام‌ها و درخواست‌ها به ترتیبی که مدیر می‌خواهد',
        changes: [
            {
                kind: 'new',
                text: 'مدیر، وام‌های صندوق را بر اساس قدیمی‌ترین قسط معوق، مبلغ معوق، مانده وام یا تاریخ وام مرتب می‌کند.',
            },
            {
                kind: 'new',
                text: 'در فهرست وام‌ها می‌شود فقط وام‌های معوق، جاری یا تسویه‌شده را دید.',
            },
            {
                kind: 'new',
                text: 'درخواست‌های وام به ترتیب قدیمی‌ترین، فوریت همراه با مدت انتظار، یا بیشترین موافقت مدیران مرتب می‌شوند.',
            },
            {
                kind: 'fix',
                text: 'درخواست‌های پذیرفته‌شده و ردشده از تازه‌ترین تصمیم شروع می‌شوند، نه از قدیمی‌ترین.',
            },
        ],
    },
    {
        version: '1.1.0',
        date: '2026-09-13',
        title: 'درخواست وام از نو، و از این به بعد خبر هر نسخهٔ تازه',
        changes: [
            {
                kind: 'new',
                text: 'هر بار همیان به‌روز شود، همین‌جا می‌گوییم چه چیزی تازه شده است.',
            },
            {
                kind: 'new',
                text: 'درخواست وام قدم‌به‌قدم ثبت می‌شود و مدیر هم می‌تواند برای عضو درخواست ثبت کند.',
            },
            {
                kind: 'new',
                text: 'هر درخواست وام صفحهٔ گفت‌وگوی خودش را دارد و مدیران نظرشان را همان‌جا ثبت می‌کنند.',
            },
            {
                kind: 'new',
                text: 'مدیر صندوق انتخاب می‌کند گزارش وام‌ها هفتگی برسد یا ماهانه.',
            },
            {
                kind: 'better',
                text: 'پشتیبان هوشمند جوابش را همان لحظه که می‌نویسد نشان می‌دهد.',
            },
            {
                kind: 'better',
                text: 'نام صندوق‌ها در فهرست، کامل و در دو خط خوانده می‌شود.',
            },
            {
                kind: 'fix',
                text: 'بعد از تمام‌شدن یک کار، دکمهٔ «عقب» دیگر شما را به همان فرم برنمی‌گرداند.',
            },
        ],
    },
]

export const APP_VERSION = RELEASES[0].version
